import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail, Lock, User, Eye, EyeOff, Loader2, CheckCircle, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

// Use empty string for same-origin API calls (works on both preview and production)
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export default function AuthModal({ isOpen, onClose, onSuccess, initialMode = "login" }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode); // "login", "register", "verification_sent", or "forgot_password"
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setFormData({ name: "", email: "", password: "", confirmPassword: "" });
      setErrors({});
      setVerificationEmail("");
      setForgotPasswordSent(false);
    }
  }, [isOpen, initialMode]);

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (mode === "register" && !formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    
    if (mode !== "forgot_password") {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      }
    }
    
    if (mode === "register" && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" 
        ? { name: formData.name, email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password };
      
      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });
      
      // Handle 403 specifically for email verification (check status before parsing)
      if (response.status === 403) {
        // For verification-required responses, show verification screen
        setVerificationEmail(formData.email);
        setMode("verification_sent");
        toast.info("Please verify your email. A new verification link has been sent.");
        return;
      }
      
      // Parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error("JSON parsing error:", jsonError);
        throw new Error("Server error. Please try again later.");
      }
      
      // Handle verification required response (from registration)
      if (data.requires_verification) {
        setVerificationEmail(data.email || formData.email);
        setMode("verification_sent");
        toast.success("Verification email sent! Check your inbox.");
        return;
      }
      
      if (!response.ok) {
        throw new Error(data.detail || "Authentication failed");
      }
      
      toast.success(mode === "register" ? "Account created successfully!" : "Welcome back!");
      
      if (onSuccess) {
        onSuccess(data);
      }
      
      onClose();
      
      // Redirect to welcome page for new registrations
      if (mode === "register") {
        navigate("/welcome");
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail || !formData.password) {
      toast.error("Please enter your password to resend verification");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: verificationEmail, 
          password: formData.password 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to resend verification");
      }
      
      toast.success("Verification email sent!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setErrors({ email: "Please enter your email address" });
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to send reset email");
      }
      
      setForgotPasswordSent(true);
      toast.success("If an account exists, a reset link has been sent!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setFormData({ name: "", email: "", password: "", confirmPassword: "" });
    setErrors({});
    setVerificationEmail("");
    setForgotPasswordSent(false);
  };

  // Verification sent view
  if (mode === "verification_sent") {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
          <DialogTitle className="sr-only">Verify Your Email</DialogTitle>
          <div className="p-6 sm:p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Mail className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2">Check Your Email</h2>
            <p className="text-muted-foreground text-sm mb-6">
              We've sent a verification link to<br />
              <span className="font-medium text-foreground">{verificationEmail}</span>
            </p>
            
            <div className="bg-muted/50 rounded-xl p-4 mb-6 text-sm text-muted-foreground">
              <p>Click the link in the email to verify your account and start coding!</p>
              <p className="mt-2">The link expires in 24 hours.</p>
            </div>
            
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full h-11 rounded-xl gap-2"
                onClick={handleResendVerification}
                disabled={loading}
                data-testid="resend-verification-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Resend Verification Email
              </Button>
              
              <Button
                variant="ghost"
                className="w-full h-11 rounded-xl"
                onClick={() => switchMode("login")}
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-6">
              Didn't receive the email? Check your spam folder.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Forgot password view
  if (mode === "forgot_password") {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
          <DialogTitle className="sr-only">Reset Password</DialogTitle>
          <div className="p-6 sm:p-8">
            {forgotPasswordSent ? (
              // Email sent confirmation
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                
                <h2 className="text-2xl font-bold mb-2">Check Your Email</h2>
                <p className="text-muted-foreground text-sm mb-6">
                  If an account exists for <span className="font-medium text-foreground">{formData.email}</span>, 
                  we've sent a password reset link.
                </p>
                
                <div className="bg-muted/50 rounded-xl p-4 mb-6 text-sm text-muted-foreground">
                  <p>The link expires in 1 hour.</p>
                  <p className="mt-2">Didn't receive it? Check your spam folder.</p>
                </div>
                
                <Button
                  variant="outline"
                  className="w-full h-11 rounded-xl"
                  onClick={() => switchMode("login")}
                  data-testid="back-to-login-btn"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              // Email input form
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold mb-2">Forgot Password?</h2>
                  <p className="text-muted-foreground text-sm">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>
                
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className={`h-11 pl-10 rounded-xl ${errors.email ? "border-destructive" : ""}`}
                        data-testid="forgot-email-input"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-xs text-destructive mt-1">{errors.email}</p>
                    )}
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full h-11 rounded-xl"
                    disabled={loading}
                    data-testid="send-reset-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </form>
                
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    className="text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => switchMode("login")}
                    data-testid="back-to-login-link"
                  >
                    Back to Login
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">
          {mode === "login" ? "Sign In" : "Create Account"}
        </DialogTitle>
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">
              {mode === "login" ? "Welcome Back" : "Create Account"}
            </h2>
            <p className="text-muted-foreground text-sm">
              {mode === "login" 
                ? "Sign in to save your progress and compete" 
                : "Join the coding challenge and track your progress"}
            </p>
          </div>

          {/* Google Auth Button */}
          <Button
            variant="outline"
            className="w-full h-12 gap-3 mb-4 rounded-xl"
            onClick={handleGoogleAuth}
            data-testid="google-auth-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or continue with email</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name field (register only) */}
            {mode === "register" && (
              <div>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`pl-10 h-12 rounded-xl ${errors.name ? 'border-red-500' : ''}`}
                    data-testid="register-name-input"
                  />
                </div>
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
            )}

            {/* Email field */}
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`pl-10 h-12 rounded-xl ${errors.email ? 'border-red-500' : ''}`}
                  data-testid="auth-email-input"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Password field */}
            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`pl-10 pr-10 h-12 rounded-xl ${errors.password ? 'border-red-500' : ''}`}
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              {mode === "login" && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline mt-1.5 block"
                  onClick={() => switchMode("forgot_password")}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              )}
            </div>

            {/* Confirm Password (register only) */}
            {mode === "register" && (
              <div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={`pl-10 h-12 rounded-xl ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    data-testid="register-confirm-password-input"
                  />
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {mode === "login" ? "Signing in..." : "Creating account..."}
                </>
              ) : (
                mode === "login" ? "Sign In" : "Create Account"
              )}
            </Button>
          </form>

          {/* Switch mode */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === "login" ? (
              <>
                Don't have an account?{" "}
                <button
                  onClick={() => switchMode("register")}
                  className="text-primary hover:underline font-medium"
                  data-testid="switch-to-register"
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => switchMode("login")}
                  className="text-primary hover:underline font-medium"
                  data-testid="switch-to-login"
                >
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
